import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTenant } from '../lib/tenant-context';

interface APIEndpoint {
  name: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
  requestBody?: object;
  exampleResponse?: object;
}

function getAPIs(tenantId: string): Record<string, { title: string; description: string; endpoints: APIEndpoint[] }> {
  if (tenantId === 'arcteryx') {
    return {
      vision: {
        title: 'Vision API',
        description: "Analyze Arc'teryx product images using AI to extract metadata, assess condition, and identify models.",
        endpoints: [
          {
            name: 'Analyze Image',
            method: 'POST',
            path: '/api/v1/vision/analyze',
            description: 'Analyze a product image and extract structured data.',
            requestBody: {
              image_url: '/images/products/arcteryx/beta-ar-jacket.jpeg',
              analysis_type: 'full',
            },
            exampleResponse: {
              success: true,
              analysis_type: 'full',
              result: {
                identification: { type: 'Shell Jacket', brand: "Arc'teryx", model: 'Beta AR' },
                attributes: { materials: ['GORE-TEX Pro'], colors: ['Black'], features: ['WaterTight zippers', 'Helmet-compatible hood'] },
                condition: { grade: 'A', wear_details: 'Excellent condition, minimal signs of use' },
              },
              provider: 'dgx-spark',
              processing_time_ms: 1890,
            },
          },
        ],
      },
      value: {
        title: 'Value API',
        description: "Estimate Arc'teryx product resale value using market data and condition assessment.",
        endpoints: [
          {
            name: 'Estimate Value',
            method: 'POST',
            path: '/api/v1/value/estimate',
            description: 'Get a price estimate for a product based on condition, model, and market factors.',
            requestBody: {
              product: {
                title: "Arc'teryx Beta AR Jacket",
                brand: "Arc'teryx",
                category: 'Shell Jackets',
                condition: 'A',
                original_price: 625,
              },
              market: 'us',
            },
            exampleResponse: {
              success: true,
              estimate: { low: 280, mid: 340, high: 400, currency: 'USD', confidence: 0.92 },
              factors: { condition_multiplier: 0.55, brand_premium: 1.20, category_demand: 1.10 },
              ai_insights: { demand: 'very high', trend: 'increasing', recommendation: 'Strong resale value - premium outdoor brand' },
            },
          },
        ],
      },
      enrich: {
        title: 'Product Intelligence API',
        description: "Enrich Arc'teryx product data with AI-generated embeddings, technical specs, and activity categorization.",
        endpoints: [
          {
            name: 'Enrich Product',
            method: 'POST',
            path: '/api/v1/products/enrich',
            description: 'Add AI-generated metadata to a product for better search and discovery.',
            requestBody: {
              product: {
                title: "Arc'teryx Alpha SV Jacket",
                brand: "Arc'teryx",
                category: 'Shell Jackets',
                description: 'GORE-TEX Pro Most Rugged, expedition-grade alpine shell',
              },
              enrichments: ['embeddings', 'categories', 'attributes', 'seo'],
            },
            exampleResponse: {
              success: true,
              enrichments: {
                categories: { primary: 'Outerwear', secondary: 'Shell Jackets', tertiary: 'Alpine Climbing' },
                attributes: { 
                  features: ['GORE-TEX Pro Most Rugged', 'StormHood', 'WaterTight zippers'], 
                  activities: ['Alpine Climbing', 'Ice Climbing', 'Mountaineering'],
                  gender_target: 'unisex',
                  weight: '490g',
                },
                seo: { seo_title: "Arc'teryx Alpha SV Jacket - Expedition Alpine Shell", keywords: ["Arc'teryx", 'Alpha SV', 'GORE-TEX Pro', 'alpine shell'] },
                embeddings: { model: 'bge-base-en-v1.5', dimensions: 768 },
              },
            },
          },
        ],
      },
    };
  }
  
  if (tenantId === 'nespresso') {
    return {
      vision: {
        title: 'Vision API',
        description: 'Analyze capsule images using AI to identify products and verify recyclability.',
        endpoints: [
          {
            name: 'Analyze Image',
            method: 'POST',
            path: '/api/v1/vision/analyze',
            description: 'Analyze a capsule image and identify the product line.',
            requestBody: {
              image_url: 'https://example.com/nespresso-capsule.jpg',
              analysis_type: 'identification',
            },
            exampleResponse: {
              success: true,
              analysis_type: 'identification',
              result: {
                identification: { type: 'Coffee Capsule', brand: 'Nespresso', model: 'Vertuo Intenso' },
                attributes: { line: 'Vertuo', intensity: 9, size: 'Gran Lungo', material: 'Aluminum' },
                recyclable: true,
              },
              provider: 'dgx-spark',
              processing_time_ms: 890,
            },
          },
        ],
      },
      value: {
        title: 'Value API',
        description: 'Calculate recycling rewards and environmental impact for capsule returns.',
        endpoints: [
          {
            name: 'Calculate Recycling Value',
            method: 'POST',
            path: '/api/v1/value/estimate',
            description: 'Calculate rewards for recycling a batch of capsules.',
            requestBody: {
              product: {
                type: 'capsule_batch',
                brand: 'Nespresso',
                line: 'Vertuo',
                count: 50,
              },
              market: 'us',
            },
            exampleResponse: {
              success: true,
              estimate: { points: 100, currency_equivalent: 2.50, currency: 'USD' },
              environmental_impact: { aluminum_saved_kg: 0.15, co2_offset_kg: 0.45 },
              ai_insights: { recycling_rate: 'excellent', recommendation: 'Continue regular recycling!' },
            },
          },
        ],
      },
      enrich: {
        title: 'Product Intelligence API',
        description: 'Enrich coffee product data with AI-generated taste profiles and pairing suggestions.',
        endpoints: [
          {
            name: 'Enrich Product',
            method: 'POST',
            path: '/api/v1/products/enrich',
            description: 'Add AI-generated metadata to a coffee product.',
            requestBody: {
              product: {
                title: 'Nespresso Vertuo Melozio',
                brand: 'Nespresso',
                category: 'Coffee',
                description: 'Smooth and balanced coffee with biscuity notes',
              },
              enrichments: ['embeddings', 'taste_profile', 'pairings'],
            },
            exampleResponse: {
              success: true,
              enrichments: {
                taste_profile: { intensity: 6, acidity: 'low', bitterness: 'light', body: 'smooth' },
                pairings: ['breakfast pastries', 'vanilla desserts', 'milk-based drinks'],
                embeddings: { model: 'bge-base-en-v1.5', dimensions: 768 },
              },
            },
          },
        ],
      },
    };
  } else if (tenantId === 'lego') {
    return {
      vision: {
        title: 'Vision API',
        description: 'Analyze LEGO product images using AI to identify sets, count pieces, and assess completeness.',
        endpoints: [
          {
            name: 'Analyze Image',
            method: 'POST',
            path: '/api/v1/vision/analyze',
            description: 'Analyze a LEGO product image and extract structured data.',
            requestBody: {
              image_url: 'https://example.com/lego-millennium-falcon.jpg',
              analysis_type: 'full',
            },
            exampleResponse: {
              success: true,
              analysis_type: 'full',
              result: {
                identification: { type: 'Set', brand: 'LEGO Star Wars', model: 'Millennium Falcon UCS', set_number: '75192' },
                attributes: { piece_count: 7541, minifigures: 4, theme: 'Star Wars' },
                condition: { grade: 'A', completeness: '100%', box_condition: 'No box' },
              },
              provider: 'dgx-spark',
              processing_time_ms: 2100,
            },
          },
        ],
      },
      value: {
        title: 'Value API',
        description: 'Estimate LEGO set and brick resale value using market data, rarity, and completeness.',
        endpoints: [
          {
            name: 'Estimate Value',
            method: 'POST',
            path: '/api/v1/value/estimate',
            description: 'Get a price estimate for LEGO products based on condition, completeness, and market demand.',
            requestBody: {
              product: {
                title: 'LEGO Star Wars Millennium Falcon UCS',
                brand: 'LEGO',
                set_number: '75192',
                category: 'Star Wars',
                condition: 'A',
                complete: true,
                has_box: false,
                has_instructions: true,
              },
              market: 'us',
            },
            exampleResponse: {
              success: true,
              estimate: { low: 650, mid: 720, high: 850, currency: 'USD', confidence: 0.91 },
              factors: { completeness_multiplier: 1.0, rarity_premium: 1.15, theme_demand: 1.25, no_box_penalty: 0.85 },
              ai_insights: { demand_trend: 'increasing', best_selling_platform: 'BrickLink', time_to_sell: '7-14 days' },
            },
          },
        ],
      },
      enrich: {
        title: 'Product Intelligence API',
        description: 'Enrich LEGO product data with AI-generated building difficulty, piece analysis, and collector insights.',
        endpoints: [
          {
            name: 'Enrich Product',
            method: 'POST',
            path: '/api/v1/products/enrich',
            description: 'Add AI-generated metadata to a LEGO product.',
            requestBody: {
              product: {
                title: 'LEGO Technic Porsche 911 GT3 RS',
                brand: 'LEGO Technic',
                set_number: '42056',
                piece_count: 2704,
              },
              enrichments: ['embeddings', 'building_analysis', 'collector_value'],
            },
            exampleResponse: {
              success: true,
              enrichments: {
                building_analysis: { difficulty: 'expert', estimated_build_time_hours: 8, recommended_age: '16+' },
                collector_value: { retirement_status: 'retired', appreciation_rate: '15% per year', rarity_score: 8.5 },
                theme_info: { theme: 'Technic', sub_theme: 'Ultimate Cars', licensed: true },
                embeddings: { model: 'bge-base-en-v1.5', dimensions: 768 },
              },
            },
          },
        ],
      },
    };
  }
  
  // Default: Lululemon
  return {
    vision: {
      title: 'Vision API',
      description: 'Analyze lululemon product images using AI to extract metadata, assess condition, and identify styles.',
      endpoints: [
        {
          name: 'Analyze Image',
          method: 'POST',
          path: '/api/v1/vision/analyze',
          description: 'Analyze a product image and extract structured data.',
          requestBody: {
            image_url: 'https://example.com/lululemon-align.jpg',
            analysis_type: 'full',
          },
          exampleResponse: {
            success: true,
            analysis_type: 'full',
            result: {
              identification: { type: 'Leggings', brand: 'lululemon', model: 'Align High-Rise Pant 25"' },
              attributes: { materials: ['Nulu'], colors: ['Black'], size: '6' },
              condition: { grade: 'A', wear_details: 'Like new, no pilling or wear' },
            },
            provider: 'dgx-spark',
            processing_time_ms: 1650,
          },
        },
      ],
    },
    value: {
      title: 'Value API',
      description: 'Estimate lululemon product resale value using market data and condition assessment.',
      endpoints: [
        {
          name: 'Estimate Value',
          method: 'POST',
          path: '/api/v1/value/estimate',
          description: 'Get a price estimate for a product based on condition, style, and market factors.',
          requestBody: {
            product: {
              title: 'lululemon Align High-Rise Pant 25"',
              brand: 'lululemon',
              category: 'Leggings',
              condition: 'A',
              original_price: 98,
            },
            market: 'us',
          },
          exampleResponse: {
            success: true,
            estimate: { low: 35, mid: 45, high: 55, currency: 'USD', confidence: 0.88 },
            factors: { condition_multiplier: 0.50, brand_premium: 1.10, style_demand: 1.15 },
            ai_insights: { demand: 'very high', trend: 'stable', recommendation: 'Popular style with consistent resale value' },
          },
        },
      ],
    },
    enrich: {
      title: 'Product Intelligence API',
      description: 'Enrich lululemon product data with AI-generated embeddings, activity tags, and fit information.',
      endpoints: [
        {
          name: 'Enrich Product',
          method: 'POST',
          path: '/api/v1/products/enrich',
          description: 'Add AI-generated metadata to a product for better search and discovery.',
          requestBody: {
            product: {
              title: 'lululemon Define Jacket',
              brand: 'lululemon',
              category: 'Jackets',
              description: 'Fitted Luon jacket with thumbholes for studio and street',
            },
            enrichments: ['embeddings', 'categories', 'attributes', 'seo'],
          },
          exampleResponse: {
            success: true,
            enrichments: {
              categories: { primary: 'Apparel', secondary: 'Jackets', tertiary: 'Studio Wear' },
              attributes: { 
                features: ['Luon fabric', 'Thumbholes', 'Slim fit'], 
                activities: ['Yoga', 'Studio', 'Casual'],
                gender_target: 'women',
              },
              seo: { seo_title: 'lululemon Define Jacket - Studio Layer', keywords: ['Define Jacket', 'lululemon', 'Luon', 'studio jacket'] },
              embeddings: { model: 'bge-base-en-v1.5', dimensions: 768 },
            },
          },
        },
      ],
    },
  };
}

export const Route = createFileRoute('/docs')({
  component: APIDocs,
});

function APIDocs() {
  const { currentTenant } = useTenant();
  const [activeAPI, setActiveAPI] = useState<string>('vision');
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  const APIS = getAPIs(currentTenant.id);
  const api = APIS[activeAPI];

  const runTest = async (endpoint: APIEndpoint) => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await fetch(endpoint.path, {
        method: endpoint.method,
        headers: { 'Content-Type': 'application/json' },
        body: endpoint.requestBody ? JSON.stringify(endpoint.requestBody) : undefined,
      });
      const data = await response.json();
      setTestResult(JSON.stringify(data, null, 2));
    } catch (err) {
      setTestResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in bg-[#F5F5F5] -m-6 p-8 min-h-screen">
        {/* Breadcrumb */}
        <nav className="mb-2">
          <ol className="flex items-center gap-2 text-sm" style={{ color: "#A9A9A9" }}>
            <li>
              <Link to="/docs" className="hover:text-[#171717] transition-colors">
                Documentation
              </Link>
            </li>
            <li>/</li>
            <li style={{ color: "#171717" }}>API Reference</li>
          </ol>
        </nav>

        {/* Header */}
        <div>
          <h1 className="text-3xl" style={{ color: "#171717", fontWeight: 300, lineHeight: 1.1 }}>API Reference</h1>
          <p className="text-base mt-2" style={{ color: "#A9A9A9", fontWeight: 300 }}>
            Interactive documentation for Product Intelligence Cloud APIs
          </p>
        </div>

      {/* Quick Start - dark section for prominence */}
      <div className="rounded-2xl p-6" style={{ backgroundColor: "#171717" }}>
        <h2 className="text-lg mb-3" style={{ color: "#FFFFFF", fontWeight: 300 }}>Quick Start</h2>
        <p className="text-sm mb-4" style={{ color: "#A9A9A9", fontWeight: 300 }}>
          All API requests require an API key. Include it in the Authorization header:
        </p>
        <div className="p-3 text-sm" style={{ backgroundColor: "#000000", color: "#00FBA9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>
          Authorization: Bearer pic_your_api_key_here
        </div>
        <p className="text-sm mt-3" style={{ color: "#A9A9A9", fontWeight: 300 }}>
          Base URL: <code className="px-2 py-1 rounded" style={{ backgroundColor: "#333", color: "#FFFFFF", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>https://agentic-commerce.pentatonic.com</code>
        </p>
        
        <div className="flex gap-3 mt-4 pt-4" style={{ borderTop: "1px solid #333" }}>
          <a 
            href="/api/v1/docs" 
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors"
            style={{ backgroundColor: "#00FBA9", color: "#171717" }}
          >
            Interactive API Explorer
          </a>
          <a 
            href="/api/v1/docs/openapi" 
            download="openapi.json"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors"
            style={{ backgroundColor: "transparent", color: "#FFFFFF", border: "1px solid #A9A9A9" }}
          >
            OpenAPI Spec
          </a>
        </div>
      </div>

      {/* API Navigation */}
      <div className="flex gap-2">
        {Object.entries(APIS).map(([key, { title }]) => (
          <button
            key={key}
            onClick={() => setActiveAPI(key)}
            className="px-5 py-2 rounded-full text-sm transition-colors"
            style={{ 
              backgroundColor: activeAPI === key ? "#00FBA9" : "transparent",
              color: activeAPI === key ? "#171717" : "#A9A9A9",
              border: activeAPI === key ? "none" : "1px solid #E5E5E5",
              fontWeight: 300
            }}
          >
            {title}
          </button>
        ))}
      </div>

      {/* API Details */}
      <div className="rounded-2xl p-6" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl" style={{ color: "#171717", fontWeight: 300 }}>{api.title}</h2>
            <p className="text-sm mt-1" style={{ color: "#A9A9A9", fontWeight: 300 }}>{api.description}</p>
          </div>
        </div>

        {api.endpoints.map((endpoint, idx) => (
          <div key={idx} className="border-t pt-6 mt-6" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
            <div className="flex items-center gap-3 mb-4">
              <span 
                className="px-2 py-1 rounded text-xs font-mono font-bold"
                style={{ 
                  backgroundColor: endpoint.method === 'GET' ? 'rgba(0,251,169,0.2)' : 'rgba(255,76,0,0.2)',
                  color: endpoint.method === 'GET' ? '#00FBA9' : '#FF4C00'
                }}
              >
                {endpoint.method}
              </span>
              <code className="text-sm font-mono" style={{ color: '#171717' }}>{endpoint.path}</code>
            </div>
            
            <p className="text-sm mb-4" style={{ color: '#A9A9A9' }}>{endpoint.description}</p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Request */}
              <div>
                <h4 className="text-sm font-medium mb-2" style={{ color: '#171717' }}>Request Body</h4>
                <pre 
                  className="p-4 text-xs overflow-x-auto"
                  style={{ backgroundColor: '#171717', color: '#FAFAFA' }}
                >
                  {JSON.stringify(endpoint.requestBody, null, 2)}
                </pre>
              </div>

              {/* Response */}
              <div>
                <h4 className="text-sm font-medium mb-2" style={{ color: '#171717' }}>Example Response</h4>
                <pre 
                  className="p-4 text-xs overflow-x-auto"
                  style={{ backgroundColor: '#FAFAFA', color: '#171717' }}
                >
                  {JSON.stringify(endpoint.exampleResponse, null, 2)}
                </pre>
              </div>
            </div>

            {/* Try It */}
            <div className="mt-4">
              <button
                onClick={() => runTest(endpoint)}
                disabled={testing}
                className="btn-primary text-sm"
              >
                {testing ? 'Testing...' : '▶ Try it Live'}
              </button>
              
              {testResult && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2" style={{ color: '#171717' }}>Live Response</h4>
                  <pre 
                    className="p-4 text-xs overflow-x-auto max-h-64"
                    style={{ backgroundColor: '#171717', color: '#00FBA9' }}
                  >
                    {testResult}
                  </pre>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Code Examples */}
      <div className="rounded-2xl p-6" style={{ backgroundColor: "#FFFFFF" }}>
        <h2 className="text-lg mb-4" style={{ color: '#171717', fontWeight: 300 }}>Code Examples</h2>
        
        <div className="space-y-4">
          <CodeExample 
            language="curl"
            title="cURL"
            code={currentTenant.id === 'arcteryx' ? `curl -X POST "https://agentic-commerce-control-plane.pages.dev/api/v1/value/estimate" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer pic_your_api_key" \\
  -d '{
    "product": {
      "title": "Arc'\\''teryx Beta AR Jacket",
      "brand": "Arc'\\''teryx",
      "condition": "A",
      "original_price": 625
    }
  }'` : `curl -X POST "https://agentic-commerce-control-plane.pages.dev/api/v1/value/estimate" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer pic_your_api_key" \\
  -d '{
    "product": {
      "title": "Gucci Marmont Bag",
      "brand": "Gucci",
      "condition": "B",
      "original_price": 2500
    }
  }'`}
          />
          
          <CodeExample 
            language="javascript"
            title="JavaScript / Node.js"
            code={currentTenant.id === 'arcteryx' ? `const response = await fetch(
  'https://agentic-commerce-control-plane.pages.dev/api/v1/value/estimate',
  {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer pic_your_api_key'
    },
    body: JSON.stringify({
      product: {
        title: "Arc'teryx Beta AR Jacket",
        brand: "Arc'teryx",
        condition: 'A',
        original_price: 625
      }
    })
  }
);

const data = await response.json();
console.log(data.estimate); // { low: 280, mid: 340, high: 400 }` : `const response = await fetch(
  'https://agentic-commerce-control-plane.pages.dev/api/v1/value/estimate',
  {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer pic_your_api_key'
    },
    body: JSON.stringify({
      product: {
        title: 'Gucci Marmont Bag',
        brand: 'Gucci',
        condition: 'B',
        original_price: 2500
      }
    })
  }
);

const data = await response.json();
console.log(data.estimate); // { low: 1368.5, mid: 1610, high: 1851.5 }`}
          />
          
          <CodeExample 
            language="python"
            title="Python"
            code={currentTenant.id === 'arcteryx' ? `import requests

response = requests.post(
    'https://agentic-commerce-control-plane.pages.dev/api/v1/value/estimate',
    headers={
        'Content-Type': 'application/json',
        'Authorization': 'Bearer pic_your_api_key'
    },
    json={
        'product': {
            'title': "Arc'teryx Beta AR Jacket",
            'brand': "Arc'teryx",
            'condition': 'A',
            'original_price': 625
        }
    }
)

data = response.json()
print(data['estimate'])  # {'low': 280, 'mid': 340, 'high': 400}` : `import requests

response = requests.post(
    'https://agentic-commerce-control-plane.pages.dev/api/v1/value/estimate',
    headers={
        'Content-Type': 'application/json',
        'Authorization': 'Bearer pic_your_api_key'
    },
    json={
        'product': {
            'title': 'Gucci Marmont Bag',
            'brand': 'Gucci',
            'condition': 'B',
            'original_price': 2500
        }
    }
)

data = response.json()
print(data['estimate'])  # {'low': 1368.5, 'mid': 1610, 'high': 1851.5}`}
          />
        </div>
      </div>

      {/* Rate Limits */}
      <div className="rounded-2xl p-6" style={{ backgroundColor: "#FFFFFF" }}>
        <h2 className="text-lg mb-4" style={{ color: '#171717', fontWeight: 300 }}>Rate Limits</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
              <th className="px-4 py-2 text-left" style={{ color: '#A9A9A9' }}>Tier</th>
              <th className="px-4 py-2 text-left" style={{ color: '#A9A9A9' }}>Requests/min</th>
              <th className="px-4 py-2 text-left" style={{ color: '#A9A9A9' }}>Requests/day</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
              <td className="px-4 py-3" style={{ color: '#171717' }}>Free</td>
              <td className="px-4 py-3" style={{ color: '#171717' }}>10</td>
              <td className="px-4 py-3" style={{ color: '#171717' }}>100</td>
            </tr>
            <tr className="border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
              <td className="px-4 py-3" style={{ color: '#171717' }}>Pro</td>
              <td className="px-4 py-3" style={{ color: '#171717' }}>100</td>
              <td className="px-4 py-3" style={{ color: '#171717' }}>10,000</td>
            </tr>
            <tr>
              <td className="px-4 py-3" style={{ color: '#171717' }}>Enterprise</td>
              <td className="px-4 py-3" style={{ color: '#171717' }}>Unlimited</td>
              <td className="px-4 py-3" style={{ color: '#171717' }}>Unlimited</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CodeExample({ title, code }: { language: string; title: string; code: string }) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium" style={{ color: '#171717' }}>{title}</span>
        <button 
          onClick={copyToClipboard}
          className="text-xs px-2 py-1 rounded"
          style={{ backgroundColor: '#FAFAFA', color: '#A9A9A9' }}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <pre 
        className="p-4 text-xs overflow-x-auto"
        style={{ backgroundColor: '#171717', color: '#FAFAFA' }}
      >
        {code}
      </pre>
    </div>
  );
}

